
import { logger } from "@/lib/logger";

export interface CacheOptions {
  enabled: boolean;
  duration: number; // milliseconds
}

export interface ApiRequestOptions {
  cache?: CacheOptions;
  retry?: {
    attempts: number;
    delay: number; // milliseconds
  };
}

const DEFAULT_CACHE_DURATION = 120000; // 2 minutes (increased from 60s)
const DEFAULT_RETRY_ATTEMPTS = 2;
const DEFAULT_RETRY_DELAY = 1000; // 1 second

export class BaseApiClient {
  private static cache = new Map<string, { data: any; timestamp: number }>();
  private static pendingRequests = new Map<string, Promise<any>>();

  /**
   * Makes an API request with caching, deduplication, and retry logic
   */
  public static async request<T>(
    cacheKey: string,
    requestFn: () => Promise<T>,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const cacheOptions = options.cache || { enabled: true, duration: DEFAULT_CACHE_DURATION };
    const retryOptions = options.retry || { attempts: DEFAULT_RETRY_ATTEMPTS, delay: DEFAULT_RETRY_DELAY };
    
    // Don't use cache if disabled
    if (!cacheOptions.enabled) {
      return this.executeWithRetry(requestFn, retryOptions.attempts, retryOptions.delay);
    }

    // If there's already a pending request for this key, return that promise
    if (this.pendingRequests.has(cacheKey)) {
      logger.info({
        message: `Reusing pending request for ${cacheKey}`,
      });
      return this.pendingRequests.get(cacheKey)!;
    }

    // Check if we have valid cached data
    const now = Date.now();
    const cachedItem = this.cache.get(cacheKey);
    
    if (cachedItem && now - cachedItem.timestamp < cacheOptions.duration) {
      logger.info({
        message: `Using cached data for ${cacheKey}`,
      });
      return cachedItem.data;
    }

    // Create and store the request promise with retry logic
    const requestPromise = (async () => {
      try {
        const result = await this.executeWithRetry(
          requestFn, 
          retryOptions.attempts, 
          retryOptions.delay
        );
        
        // Cache the result
        this.cache.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });
        
        return result;
      } finally {
        // Remove from pending requests when done
        setTimeout(() => {
          this.pendingRequests.delete(cacheKey);
        }, 100); // Small delay to prevent race conditions
      }
    })();

    // Store the promise in pending requests
    this.pendingRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }

  /**
   * Execute a request with retry logic
   */
  private static async executeWithRetry<T>(
    requestFn: () => Promise<T>,
    maxAttempts: number,
    delay: number
  ): Promise<T> {
    let lastError: any;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        logger.warn({
          message: `Request failed (attempt ${attempt}/${maxAttempts})`,
          error: error instanceof Error ? error : new Error(String(error))
        });
        
        if (attempt < maxAttempts) {
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
      }
    }
    
    // If we get here, all attempts failed
    throw lastError;
  }

  /**
   * Clear cache entries based on key prefix or all cache if no prefix provided
   */
  public static clearCache(keyPrefix?: string): void {
    if (keyPrefix) {
      // Clear only keys that start with the prefix
      const keysToDelete: string[] = [];
      this.cache.forEach((_, key) => {
        if (key.startsWith(keyPrefix)) {
          keysToDelete.push(key);
        }
      });
      
      keysToDelete.forEach(key => {
        this.cache.delete(key);
        this.pendingRequests.delete(key);
      });
    } else {
      // Clear all cache
      this.cache.clear();
      this.pendingRequests.clear();
    }
    
    logger.info({
      message: `Cache cleared${keyPrefix ? ` for prefix: ${keyPrefix}` : ''}`,
    });
  }
  
  /**
   * Prefetch and cache a request without waiting for the result
   */
  public static prefetch<T>(
    cacheKey: string,
    requestFn: () => Promise<T>,
    options: ApiRequestOptions = {}
  ): void {
    // Don't prefetch if already in cache or pending
    const cachedItem = this.cache.get(cacheKey);
    const cacheOptions = options.cache || { enabled: true, duration: DEFAULT_CACHE_DURATION };
    
    if (this.pendingRequests.has(cacheKey)) {
      return; // Already being fetched
    }
    
    const now = Date.now();
    if (cachedItem && now - cachedItem.timestamp < cacheOptions.duration) {
      return; // Already cached and valid
    }
    
    // Start the request but don't wait for it
    this.request(cacheKey, requestFn, options)
      .catch(error => {
        logger.error({
          message: `Prefetch failed for ${cacheKey}`,
          error: error instanceof Error ? error : new Error(String(error))
        });
      });
  }
}
